import React, { useRef, useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import IconButton from "@mui/material/IconButton";
import SendIcon from "@mui/icons-material/Send";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import { useTheme, alpha, styled } from "@mui/material";
import { useAuth } from "react-oidc-context";
import { getPresignedUploadUrl, uploadFileToS3 } from "../utils/backend-utils";

const VisuallyHiddenInput = styled('input')({
    clip: 'rect(0 0 0 0)',
    clipPath: 'inset(50%)',
    height: 1,
    overflow: 'hidden',
    position: 'absolute',
    bottom: 0,
    left: 0,
    whiteSpace: 'nowrap',
    width: 1,
});

interface ChatInputProps {
    value: string;
    onChange: (value: string) => void;
    onSubmit: (attachmentKeys?: string[], localFiles?: File[]) => void;
    disabled?: boolean;
    placeholder?: string;
    sessionId?: string;
    allowAttachments?: boolean; // Only allow attachments when plan is READY, IN_PROGRESS, or NEEDS_INPUT
    /** When true, Enter sends and Shift+Enter inserts a newline. Default: Cmd/Ctrl+Enter sends. */
    submitOnEnter?: boolean;
}

export function ChatInput({ value, onChange, onSubmit, disabled = false, placeholder = "Type your answer...", sessionId, allowAttachments = true, submitOnEnter = false }: ChatInputProps) {
    const theme = useTheme();
    const auth = useAuth();
    const inputRef = useRef<HTMLInputElement>(null);
    const [attachedFiles, setAttachedFiles] = useState<File[]>([]);
    const [isDragging, setIsDragging] = useState(false);
    const [isUploading, setIsUploading] = useState(false);

    // Focus input on mount (unless disabled)
    useEffect(() => {
        if (!disabled) {
            inputRef.current?.focus();
        }
    }, [disabled]);

    // Handle Enter key
    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key !== "Enter") return;
        if (submitOnEnter) {
            if (!e.shiftKey) {
                e.preventDefault();
                handleSubmit();
            }
            return;
        }
        if (e.metaKey || e.ctrlKey) {
            e.preventDefault();
            handleSubmit();
        }
    };

    const handleSubmit = async () => {
        if (!value.trim() && attachedFiles.length === 0) return;
        if (isUploading) return; // Prevent double submission

        try {
            let attachmentKeys: string[] = [];
            const filesToSend = [...attachedFiles]; // Keep a copy for display

            // Upload files if any
            if (attachedFiles.length > 0) {
                setIsUploading(true);
                attachmentKeys = await uploadFiles(attachedFiles);
            }

            // Pass both keys (for backend) and local files (for immediate display)
            onSubmit(
                attachmentKeys.length > 0 ? attachmentKeys : undefined,
                filesToSend.length > 0 ? filesToSend : undefined
            );
            setAttachedFiles([]);
        } catch (error) {
            console.error('Failed to upload files:', error);
            // Continue with submission even if upload fails
            onSubmit(undefined, undefined);
        } finally {
            setIsUploading(false);
        }
    };

    const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files) {
            const newFiles = Array.from(e.target.files);
            setAttachedFiles((prev) => [...prev, ...newFiles]);
        }
    };

    const handleRemoveFile = (index: number) => {
        setAttachedFiles((prev) => prev.filter((_, i) => i !== index));
    };

    const handleDragOver = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(true);
    };

    const handleDragLeave = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);

        if (e.dataTransfer.files) {
            const newFiles = Array.from(e.dataTransfer.files);
            setAttachedFiles((prev) => [...prev, ...newFiles]);
        }
    };

    const handlePaste = (e: React.ClipboardEvent) => {
        const items = e.clipboardData?.items;
        if (items) {
            const files: File[] = [];
            for (let i = 0; i < items.length; i++) {
                if (items[i].kind === "file") {
                    const file = items[i].getAsFile();
                    if (file) {
                        files.push(file);
                    }
                }
            }
            if (files.length > 0) {
                setAttachedFiles((prev) => [...prev, ...files]);
            }
        }
    };

    const uploadFiles = async (files: File[]): Promise<string[]> => {
        const uploadedKeys: string[] = [];

        for (const file of files) {
            try {
                // Step 1: Get presigned URL
                const { upload_url, key } = await getPresignedUploadUrl(file.name, auth);

                // Step 2: Upload to S3
                await uploadFileToS3(upload_url, file);

                // Step 3: Store the key for the reply payload
                uploadedKeys.push(key);
            } catch (error) {
                console.error(`Failed to upload file ${file.name}:`, error);
                // Continue with other files
            }
        }

        return uploadedKeys;
    };

    return (
        <Box
            sx={{
                borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                bgcolor: theme.palette.background.paper,
                px: 2,
                py: 2,
                pb: 'max(16px, env(safe-area-inset-bottom, 16px))', // Safe area for mobile notches
                '@media (min-width: 600px)': {
                    px: 3,
                    py: 2.5,
                    pb: 2.5,
                },
            }}
        >
            <Box
                sx={{
                    maxWidth: '960px',
                    mx: 'auto',
                }}
            >
                {/* Attached Files Display */}
                {allowAttachments && attachedFiles.length > 0 && (
                    <Box
                        sx={{
                            mb: 1,
                            display: "flex",
                            flexWrap: "wrap",
                            gap: 1,
                        }}
                    >
                        {attachedFiles.map((file, index) => {
                            const isImage = file.type.startsWith("image/");
                            const imageUrl = isImage ? URL.createObjectURL(file) : null;
                            
                            return isImage && imageUrl ? (
                                <Box
                                    key={index}
                                    sx={{
                                        position: "relative",
                                        width: 80,
                                        height: 80,
                                        borderRadius: 2,
                                        overflow: "hidden",
                                        border: `2px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                                    }}
                                >
                                    <Box
                                        component="img"
                                        src={imageUrl}
                                        alt={file.name}
                                        sx={{
                                            width: "100%",
                                            height: "100%",
                                            objectFit: "cover",
                                        }}
                                    />
                                    <IconButton
                                        size="small"
                                        onClick={() => handleRemoveFile(index)}
                                        sx={{
                                            position: "absolute",
                                            top: 2,
                                            right: 2,
                                            bgcolor: alpha(theme.palette.background.paper, 0.9),
                                            "&:hover": {
                                                bgcolor: theme.palette.background.paper,
                                            },
                                            width: 20,
                                            height: 20,
                                            padding: 0,
                                        }}
                                    >
                                        <CloseIcon sx={{ fontSize: 14 }} />
                                    </IconButton>
                                </Box>
                            ) : (
                                <Chip
                                    key={index}
                                    icon={<AttachFileIcon />}
                                    label={file.name}
                                    onDelete={() => handleRemoveFile(index)}
                                    deleteIcon={<CloseIcon />}
                                    size="small"
                                    sx={{
                                        maxWidth: "200px",
                                        bgcolor: alpha(theme.palette.primary.main, 0.1),
                                        "& .MuiChip-label": {
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                        },
                                    }}
                                />
                            );
                        })}
                    </Box>
                )}

                <Box
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    sx={{
                        display: 'flex',
                        gap: 1,
                        alignItems: 'flex-end',
                        position: 'relative',
                        borderRadius: '12px',
                        border: `2px solid ${isDragging ? theme.palette.primary.main : 'transparent'}`,
                        bgcolor: isDragging ? alpha(theme.palette.primary.main, 0.05) : 'transparent',
                        transition: 'all 0.3s ease',
                        p: isDragging ? 1 : 0,
                    }}
                >
                    {allowAttachments && (
                        <IconButton
                            component="label"
                            disabled={disabled}
                            size="small"
                            sx={{
                                color: theme.palette.text.secondary,
                                alignSelf: 'center',
                                "&:hover": {
                                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                                    color: theme.palette.primary.main,
                                },
                            }}
                        >
                            <AddIcon />
                            <VisuallyHiddenInput
                                type="file"
                                multiple
                                accept="image/*,application/pdf,.doc,.docx,.txt"
                                onChange={handleFileSelect}
                            />
                        </IconButton>
                    )}
                    <TextField
                        inputRef={inputRef}
                        fullWidth
                        multiline
                        maxRows={10}
                        placeholder={placeholder}
                        value={value}
                        onChange={(e) => onChange(e.target.value)}
                        onKeyDown={handleKeyDown}
                        onPaste={handlePaste}
                        variant="outlined"
                        size="small"
                        disabled={disabled}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                borderRadius: '12px',
                                bgcolor: alpha(theme.palette.background.default, 0.5),
                            },
                        }}
                    />
                    <Button
                        variant="contained"
                        onClick={handleSubmit}
                        disabled={disabled || isUploading || (!value.trim() && attachedFiles.length === 0)}
                        sx={{
                            minWidth: '48px',
                            height: '40px',
                            borderRadius: '12px',
                            alignSelf: 'center',
                        }}
                    >
                        {isUploading ? (
                            <CircularProgress size={20} color="inherit" />
                        ) : (
                            <SendIcon fontSize="small" />
                        )}
                    </Button>
                </Box>
            </Box>
        </Box>
    );
}
